
import { EOrientation } from "$src/CGS/domain/dto/core/EOrientation";
import { EJoinType } from "$src/CGS/domain/dto/core/FrontElementParameter/EJoinType";
import { DrawCabinetFront } from "$src/CGS/domain/dto/article/Cabinet/DrawCabinetFront";
import { DrawCabinet } from "$src/CGS/domain/dto/article/Cabinet/DrawCabinet";
import { DrawCabinetFrontElement } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElement";
import { DrawCabinetFrontElementGroup } from "$src/CGS/domain/dto/article/Cabinet/FrontElement/DrawCabinetFrontElementGroup";
import { EFrontElement } from "$src/CGS/domain/dto/core/EFrontElement";
import { LengthValueMillimeter } from "$src/CGS/domain/dto/core/Length/LengthValueMillimeter";
import { Vector3 } from "three";
import { CabinetGeneratorErrorRegistry } from "../../Errors/CabinetGeneratorErrorRegistry";
import { ICabinetShapeGenerator } from "../../ShapeGenerators/ICabinetShapeGenerator";
import { FrontFaceMetadata } from "../FrontFaceMetadata";
import { FrontMetadata } from "../FrontMetadata";
import { LengthMaths } from "$src/CGS/domain/dto/core/Length/LengthMaths";
import { FrontElementMetadataBuilder } from "./FrontElementMetadataBuilder";
import { FrontElementComputation } from "../Compute/FrontElementComputation";
import { ComputationElementBuilder } from "../Compute/Builder/ComputationElementBuilder";
import { FrontFace } from "$src/CGS/domain/dto/core/FrontFace";
import { FrontFaceId } from "$src/CGS/domain/dto/core/FrontFaceId";

export class FrontFaceMetadataBuilder {
    private _drawCabinet!: DrawCabinet;
    private _frontElementBuilder!: FrontElementMetadataBuilder;
    private _cabinetShapeGenerator: ICabinetShapeGenerator;
    public ErrorRegistry: CabinetGeneratorErrorRegistry;

    constructor(cabinetShapeGenerator: ICabinetShapeGenerator) {
        this._cabinetShapeGenerator = cabinetShapeGenerator;
        this.ErrorRegistry = new CabinetGeneratorErrorRegistry();
    }

    public Build(drawCabinet: DrawCabinet): FrontFaceMetadata[] {
        this._drawCabinet = drawCabinet;
        this._frontElementBuilder = new FrontElementMetadataBuilder(this._drawCabinet, this.ErrorRegistry);

        const computationElementBuilder = new ComputationElementBuilder(drawCabinet.Constants, this._cabinetShapeGenerator);
        const frontFaces: FrontFaceMetadata[] = [];

        for (const drawCabinetFront of drawCabinet.Fronts) {
            if (drawCabinetFront.Faces.map(f => f.Index).filter((value, index, self) => self.indexOf(value) === index).length === 1) {
                const includeCabinet = drawCabinetFront.Faces.some(f => !f.IsPlinth);
                const includePlinth = drawCabinetFront.Faces.some(f => f.IsPlinth);

                const frontFaceIndex = drawCabinetFront.Faces[0].Index;
                const frontFace = new FrontFaceMetadata();
                frontFace.Index = frontFaceIndex;
                const frontElements = this.BuildSingleFace(frontFace, computationElementBuilder.build(drawCabinetFront.Element, drawCabinet.Range?.IsWithGaps ?? false), includeCabinet, includePlinth);
                frontFace.FrontElements = frontElements;
                frontFaces.push(frontFace);
            } else if (drawCabinetFront.Element instanceof DrawCabinetFrontElementGroup
                && drawCabinetFront.Element.Orientation === EOrientation.Horizontal) {
                const elementToProcess = drawCabinetFront.Element.Elements.slice();
                const processedFaceIndex: number[] = [];

                const groupedFaces: Map<number, FrontFaceId[]> = drawCabinetFront.Faces.reduce((acc, face) => {
                    if (!acc.has(face.Index)) {
                        acc.set(face.Index, []);
                    }
                    acc.get(face.Index)?.push(face);
                    return acc;
                }, new Map<number, FrontFaceId[]>());

                for (const [index, frontFaceGroup] of groupedFaces.entries()) {
                    
                    const includeCabinet = frontFaceGroup.some(f => !f.IsPlinth);
                    const includePlinth = frontFaceGroup.some(f => f.IsPlinth);

                    processedFaceIndex.push(frontFaceGroup[0].Index);
                    const isLastIndex = drawCabinetFront.Faces.every(f => processedFaceIndex.includes(f.Index));

                    let drawCabinetFrontElement: DrawCabinetFrontElement | null = null;
                    if (isLastIndex && elementToProcess.length > 1) {
                        let group = new DrawCabinetFrontElementGroup();
                        group.Orientation = EOrientation.Horizontal;
                        group.JoinType = EJoinType.N;
                        (group as DrawCabinetFrontElementGroup).Elements = elementToProcess;
                        drawCabinetFrontElement = group;
                    } else if (elementToProcess.length > 0) {
                        drawCabinetFrontElement = elementToProcess.shift()!;
                    }

                    if (drawCabinetFrontElement !== null) {
                        const frontFace = new FrontFaceMetadata();
                        frontFace.Index = frontFaceGroup[0].Index;
                        const frontElements = this.BuildSingleFace(frontFace, computationElementBuilder.build(drawCabinetFrontElement, drawCabinet.Range?.IsWithGaps ?? false), includeCabinet, includePlinth);
                        frontFace.FrontElements = frontElements;
                        frontFaces.push(frontFace);
                    }
                }
            } else if (
                drawCabinet.Fronts.length === 1
                && drawCabinetFront.Element instanceof DrawCabinetFrontElement
                && drawCabinetFront.Element.FrontElement === EFrontElement.E
            ) {
                const includeCabinet = drawCabinetFront.Faces.some(f => !f.IsPlinth);
                const includePlinth = drawCabinetFront.Faces.some(f => f.IsPlinth);

                const frontFaceIndex = drawCabinetFront.Faces[0].Index;
                const frontFaceMetadatas = drawCabinetFront.Faces.map(f => {
                    const frontFace = new FrontFaceMetadata();
                    frontFace.Index = f.Index;
                    return frontFace;
                });

                const frontElements = this.BuildMultiFace(frontFaceMetadatas, computationElementBuilder.build(drawCabinetFront.Element, drawCabinet.Range?.IsWithGaps ?? false), includeCabinet, includePlinth);
                frontFaceMetadatas[0].FrontElements = frontElements;
                frontFaces.push(...frontFaceMetadatas);
                }
            }
            return frontFaces;
        }
        public BuildSingleFace(frontFace: FrontFaceMetadata, element: FrontElementComputation, cabinetIncluded = true, plinthIncluded = false): FrontMetadata[] {
            return this.BuildMultiFace([frontFace], element, cabinetIncluded, plinthIncluded);
        }
        
        public BuildMultiFace(frontFaces: FrontFaceMetadata[], element: FrontElementComputation, cabinetIncluded = true, plinthIncluded = false): FrontMetadata[] {
            let availWidth = LengthMaths.subtract(this._cabinetShapeGenerator.FrontFaceSize(frontFaces[0].Index), LengthMaths.multiply(this._drawCabinet.Constants.SideBoxGap, 2));
        
            let availHeight = LengthMaths.Zero;
            let yStartPosition = this._drawCabinet.Constants.GapBottomBox;
            let zStartPosition = LengthMaths.Zero;
        
            if (cabinetIncluded) {
                availHeight = LengthMaths.add(availHeight, LengthMaths.getLengthValue(this._drawCabinet.CabinetShape.Height));
            }
        
            if (plinthIncluded) {
                availHeight = LengthMaths.add(availHeight, this._drawCabinet.Constants.PlinthHeight);
                yStartPosition = LengthMaths.subtract(yStartPosition, this._drawCabinet.Constants.PlinthHeight);
        
                if (!cabinetIncluded) {
                    zStartPosition = LengthMaths.add(zStartPosition, this._drawCabinet.Constants.PlinthDepth);
                }
            }
        
            availHeight = LengthMaths.subtract(availHeight, LengthMaths.add(this._drawCabinet.Constants.GapBottomBox, this._drawCabinet.Constants.GapTopBox));
        
            const surplusLength = new LengthValueMillimeter(0);
            const startPos = new Vector3(this._drawCabinet.Constants.SideBoxGap.meter, yStartPosition.meter, zStartPosition.meter);
        
            return this._frontElementBuilder.BuildFrontElements(frontFaces, element, availWidth, availHeight, surplusLength, startPos);
        }
    }